import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setNewMyTasks } from "../../../../../actions/task";
import { setFlowElements, toggleApproval } from "../../../../../actions/taskSidebar";
import { icon, label, notifyIcon, number, quote, tooltip } from "../../../../../config";
import { editWorkflowElement } from "../../../../../shared/services/workflow.services";
import store from "../../../../../store";
import { getNotification } from "../../../../../utils/common";
import { useKanbanUpdates } from "../../../../KanbanView/hooks/kanban.hooks";
import { getNotificationsCount } from "../../../../Notifications/notifications.service";
import { updateAssignmentId } from "../../../../Tasks/tasks.helper";
import { getTaskWorkflowDetails, handleTaskKeyUpdate, updateTaskSidebar } from "../../../../Tasks/tasks.service";
import { planMyDayEffects } from "../../../TaskSidebarHeader/TaskSidebarHeader.helper";
import { getCurrentActiveProject } from "../../../sidebar.common";
import { checkFlowInsertion, getAssignmentAndApprovalPayload, getRelatedAssignedType } from "../../../sidebar.helper";
import {
  getAssignmentNotification,
  getInitialRelatedAssignId,
  initialAssignmentValue,
  isExternalAssignment,
  isInternalUserChanged,
} from "../taskDetail.helper";
import { TaskAssignmentContext } from "./taskAssignment.context";
import { Tooltip } from "@progress/kendo-react-tooltip";

/**
 * Task submit component
 * Parent component => TaskAssignment
 * @returns JSX
 */
const TaskSubmit = () => {
  const [tickDisabled, setTickDisabled] = useState(false);
  const [initialRelatedAssignmentType, setInitialRelatedAssignmentType] = useState(null);
  const [initialRelatedAssignmentId, setInitialRelatedAssignmentId] = useState(null);

  const assignmentState = useContext(TaskAssignmentContext);

  const {
    assignedId,
    relatedAssignmentId,
    setAssignmentType,
    setAssignedId,
    setRelatedAssignmentId,
    setDelegationType,
    setExternalUser,
    isDisabled,
    workflowAssignmentType,
    setWorkflowAssignmentType,
    setRelatedAssignmentList,
    assignmentType: assignedType,
    matchingFlowElement,
  } = assignmentState;

  const { isPlanMyDayActive } = useSelector((state) => state.planMyDay);
  /**
   * disables tick icon if all any value is not found
   * @author {Prachi Jain}
   */
  useEffect(() => {
    if (!assignedId?.value || !assignedType?.key || !workflowAssignmentType?.value) {
      setTickDisabled(true);
    } else {
      setTickDisabled(false);
    }
  }, [assignedId, assignedType, relatedAssignmentId, workflowAssignmentType]);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { defaultDetails, allTasks } = useSelector((state) => state.tasks);
  const { task, isNewTask, taskUrl, isApproval, flowElements } = useSelector((task) => task.taskSidebar);
  const { selectedView } = useSelector((state) => state.tasks);
  const { updateKanbanAssignee } = useKanbanUpdates();
  const initialAssignmentPayload = {
    setAssignmentType,
    setAssignedId,
    setWorkflowAssignmentType,
    setRelatedAssignmentId,
    setExternalUser,
    setDelegationType,
    setRelatedAssignmentList,
    defaultDetails,
    task,
    isNewTask,
  };

  // set user icon in DD3 and assignee in DD4 for generic projects
  useEffect(() => {
    (async () => {
      if (isNewTask) return;
      const assignedTypePayload = {
        AssignedType: task?.CurrentAssignedType,
        AssignedId: task?.CurrentAssignedId,
        RelatedAssignedType: task?.CurrentRelatedAssignedType,
      };
      const currentRelatedAssignedType = getRelatedAssignedType(assignedTypePayload);
      setInitialRelatedAssignmentType(currentRelatedAssignedType);
      const requiredId = await getInitialRelatedAssignId({ task, defaultDetails });
      setInitialRelatedAssignmentId(requiredId);
    })();
  }, [task?.taskId, task?.Assignee]);


  const getResetValue = (resetSidebar, isSelfApproval, isPlanMyDayActive, externalAssignment) => {
    if (isPlanMyDayActive) return false;
    if(externalAssignment) return false;
    return !resetSidebar || isSelfApproval;
  };


  /**
   * update the task assignment
   * workflowAssignmentType = 5 => assigned to workflow (DD3)
   * @param {Void}
   * @returns {Void}
   * @author Himanshu Negi
   */
  const updateTaskAssignment = async () => {
    setTickDisabled(true);
    const taskState = { task, isApproval, taskUrl, user };
    if (!assignedId?.value) {
      getNotification(label.SELECT_USER, notifyIcon.WARNING_ICON);
      setTickDisabled(false);
      return;
    }
    const payload = getAssignmentAndApprovalPayload(taskState, assignmentState);
    const resetSidebar = isInternalUserChanged(task, assignmentState, isApproval);
    const externalAssignment = isExternalAssignment(task, assignmentState, isApproval);
    const { currentProjectIndex } = getCurrentActiveProject(flowElements);
    const checkInsertionPayload = {
      ...payload,
      taskAssignmentId: payload.assignedId,
      assignmentProjectId: assignedType.key === number.TWO ? assignedId?.myProjectId : assignedId?.value,
    };
    const isValidInsertion = await checkFlowInsertion(flowElements, currentProjectIndex + number.ONE, checkInsertionPayload);
    if (isValidInsertion && externalAssignment) {
      getNotification(quote.CAN_NOT_COEXIST, notifyIcon.WARNING_ICON);
      setTickDisabled(false);
      return;
    }

    const isSelfApproval =
      task?.CurrentAssignedId === payload.assignedId &&
      task?.CurrentAssignedType === payload.assignedType &&
      task?.CurrentRelatedAssignedId === payload.relatedAssignedId &&
      task?.CurrentRelatedAssignedType === payload.relatedAssignedType &&
      isApproval;

    payload.isSelfApproval = isSelfApproval;
    let reset = getResetValue(resetSidebar, isSelfApproval, isPlanMyDayActive, externalAssignment);
    
    const response = await dispatch(updateTaskSidebar(payload, reset));

    if(externalAssignment) {
      await dispatch(getTaskWorkflowDetails({ taskId: payload.taskId }));
    }

    if (resetSidebar) {
      setInitialRelatedAssignmentId(relatedAssignmentId);
      await handleTaskKeyUpdate(isNewTask, "assignee", relatedAssignmentId?.value);
      await handleTaskKeyUpdate(isNewTask, "CurrentRelatedAssignedId", relatedAssignmentId?.value);
    }

    if (
      payload?.relatedAssignedType &&
      payload?.relatedAssignedType === number.TWO &&
      task?.CurrentAssignedId === payload.assignedId &&
      task?.AssignedType === payload.assignedType
    ) {
      const newAssigneePayload = updateAssignmentId(
        allTasks,
        defaultDetails?.assigneeList,
        payload?.relatedAssignedId,
        payload?.assignedBy,
        task?.taskId
      );
      dispatch(setNewMyTasks(newAssigneePayload));
    }
    if (isPlanMyDayActive) {
      planMyDayEffects();
      return;
    }
    if ((!resetSidebar || isApproval) && response) {
      dispatch(getTaskWorkflowDetails({ taskId: payload.taskId }));
      initialAssignmentValue(initialAssignmentPayload);
      const notificationVerbiage = getAssignmentNotification(task, assignmentState, isApproval);
      notificationVerbiage && getNotification(notificationVerbiage, notifyIcon.SUCCESS_ICON);
      setTickDisabled(false);
    }
    if (matchingFlowElement) {
      matchingFlowElement.workflowAssignmentId = relatedAssignmentId?.value;
      matchingFlowElement.workflowAssignmentType = workflowAssignmentType?.value;
      matchingFlowElement.flowElementId = matchingFlowElement.id;
      matchingFlowElement.flowDetail = relatedAssignmentId?.label;
      const editResponse = await dispatch(editWorkflowElement(matchingFlowElement));
      const currentState = store.getState();
      const latestFlowElements = currentState.taskSidebar?.flowElements || flowElements;
      /**
       * Update flow elements in Redux state to reflect the changes in real-time
       * @author Muskan Thakur
       */
      if (editResponse && latestFlowElements) {
        const updatedFlowElements = latestFlowElements.map((flow) =>
          flow?.id === matchingFlowElement.id
            ? {
                ...flow,
                workflowAssignmentId: relatedAssignmentId?.value,
                workflowAssignmentType: workflowAssignmentType?.value,
                flowDetail: relatedAssignmentId?.label,
                ...(editResponse.WorkflowIcon && { workflowIcon: editResponse.WorkflowIcon }),
              }
            : flow
        );
        dispatch(setFlowElements(updatedFlowElements));
      }
    }
    isApproval && dispatch(toggleApproval(false));
    isApproval && dispatch(getNotificationsCount(user.id));
    if (resetSidebar && selectedView === label.PIVOT) updateKanbanAssignee(task?.taskId, relatedAssignmentId?.value);
  };

  /**
   * handles clear assigned value button
   * @author Muskan Thakur
   */
  const handleClearAssigned = () => {
    initialAssignmentValue(initialAssignmentPayload);
  };

  return (
    <React.Fragment>
      <div className='task-assignment-submit'>
        {(relatedAssignmentId?.value !== initialRelatedAssignmentId?.value ||
          assignedId?.value !== task.CurrentAssignedId ||
          workflowAssignmentType?.value !== initialRelatedAssignmentType ||
          isApproval) && (
          <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
            <div className='d-flex align-items-center justify-content-center'>
              {!isDisabled && !isNewTask && (
                <span className='square-icon square-icon-extra-small' onClick={handleClearAssigned}>{icon.CLOSE}</span>              
              )}
              {!isNewTask && !isDisabled && (
                <button
                  className='ml-1 square-icon square-icon-extra-small border-0'
                  disabled={tickDisabled}
                  onClick={updateTaskAssignment}
                  title={tooltip.CLICK_TO_ASSIGN}>
                  {icon.CHECKBOX}
                </button>
              )}
            </div>
          </Tooltip>
        )}
      </div>
    </React.Fragment>
  );
};

export default TaskSubmit;
